import { SortingState } from "@tanstack/react-table";
import { Axios } from "api/axios";
import TableContainer from "Common/TableContainer";
import LoaderErrorWrapper from "Components/LoaderErrorWrapper/LoaderErrorWrapper";
import SearchBox from "Components/SearchBox/SearchBox";
import { app_modules } from "helpers/apps_modules_list";
import {
  Fragment,
  useEffect,
  useRef,
  useState
} from "react";
import { Button, Card, Col, Container, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { createSelector } from "reselect";
import {
  exportCategoryCSV,
  fetchBrandsAttributes as OnfetchAttributes
} from "slices/attributes/thunk";
import {
  fetchLatestPermissions
} from "slices/auth/login/thunk";

const AttributesLList = () => {
  document.title = "Attributes";
  const dispatch = useDispatch<any>();

  // Redux selector for category state
  const selectBrands = createSelector(
    (state: any) => state.brand, //
    (brand) => ({
      categories: brand.brandList ?? [],
      rowCount: brand.rowCount ?? 0,
      loading: brand.loading,
      error: brand.error,
    })
  );

  const { categories, rowCount, loading, error } = useSelector(selectBrands);

  const user = useSelector((state: any) => state.Login.user);
  const user_permissions = user?.permissions || [];
  const master_permissions = user_permissions?.find(
    (item: any) => item?.apps_name === app_modules?.master?.app_name
  );
  const attributes_permissions = master_permissions?.modules_permissions?.find(
    (item: any) =>
      item?.modules_name === app_modules?.master?.modules?.attribute
  );

  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        await dispatch(fetchLatestPermissions()).unwrap();
      } catch (error) {
        console.error("Failed to fetch latest permissions", error);
      }
    };
    fetchPermissions();
  }, [dispatch]);

  //*// For redirecting back to this page and get the same paginated pageIndex
  const [searchParams, setSearchParams] = useSearchParams();
  const pageParam = parseInt(searchParams.get("page") || "0");

  const [pageIndex, setPageIndex] = useState(pageParam);
  const [pageSize, setPageSize] = useState(10);
  const [data, setData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(rowCount);
  const [isStatusUpdateLoading, setIsStatusUpdateLoading] =
    useState<boolean>(false);

  // State for edit functionality
  const [editCategory, setEditCategory] = useState<boolean>(false);
  const [columnFilters, setColumnFilters] = useState<any>();
  const [edit, setEdit] = useState<any>();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [filterValue, setFilterValue] = useState<any[]>([]);
  const sortField = sorting[0]?.id ?? "";
  const sortDirection = sorting[0]?.desc ? "desc" : "asc";
  const navigate = useNavigate();
  useEffect(() => {
    dispatch(
      OnfetchAttributes({
        page: pageIndex + 1,
        pageSize,
        sort: sortField,
        order: sortField ? sortDirection : "",
        search,
        filters: filterValue,
      })
    );
  }, [dispatch, pageIndex, pageSize, sorting, search, sortField, sortDirection]);

  const prevFilters = useRef(filterValue);

  useEffect(() => {
    if (JSON.stringify(prevFilters.current) !== JSON.stringify(filterValue)) {
      prevFilters.current = filterValue;
      dispatch(
        OnfetchAttributes({
          page: 1, // Always use page 1 when filters change
          pageSize,
          sort: sortField,
          order: sortField ? sortDirection : "",
          search,
          filters: filterValue,
        })
      );
    }
  }, [filterValue]);

  useEffect(() => {
    setData(categories);
  }, [categories]);

  //*// All pagination Issues(resolved) when redirecting from the edit or view page
  // Reset pagenumber to 0 when not first render
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    handlePageChange(0);
  }, [sorting, pageSize, columnFilters, search]);
  const handlePageChange = (newPage: number) => {
    setPageIndex(newPage);
    setSearchParams({ page: newPage.toString() });
  };
  // Debounced search
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setSearch(searchInput);
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchInput]);
  //*//

  //*// Fetch Permissions
  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        const result = await dispatch(fetchLatestPermissions()).unwrap();
      } catch (error) {
        console.error("Failed to fetch latest permissions", error);
        // toast.error("Failed to fetch latest permissions");
      }
    };
    fetchPermissions();
  }, [dispatch]);

  // Handle edit action
  const handleEdit = (item: any) => {
    setEditCategory(!editCategory);
    setEdit({
      id: item.id,
      categoryName: item.category_name,
      state: item.state_name,
      status: item.is_active,
      createdBy: item.created_by,
      updatedBy: item.updated_by,
      createDate: item.created_at,
      updatedDate: item.updated_at,
    });
  };
  const formatDate = (dateString?: string) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };
  const handleStatusToggle = async (pkg: any) => {
    try {
      const updatedStatus = !pkg.status;

      const payload = {
        brand_name: pkg.brand_name,
        status: updatedStatus,
      };

      setIsStatusUpdateLoading(true);

      await Axios.patch(
        `api/attributes-master/edit-status/${pkg.attribute_id}/`,
        payload
      );

      toast.success(
        `Attribute ${updatedStatus ? "activated" : "deactivated"} successfully`
      );

      dispatch(
        OnfetchAttributes({
          page: pageIndex + 1,
          pageSize,
          sort: sortField,
          order: sortField ? sortDirection : "",
          search,
          filters: filterValue,
        })
      );
    } catch (err: any) {
      toast.error("Failed to update status");
    } finally {
      setIsStatusUpdateLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      // const { blob, filename } = await exportCategoryCSV();
      const { blob, filename } = await exportCategoryCSV({
        search,
        filters: filterValue,
      });

      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Export failed", error);
      toast.error("Failed to export CSV");
    }
  };

  const DynamicAction =
    attributes_permissions?.permissions?.Update === true
      ? [
          {
            header: "Action",
            enableColumnFilter: false,
            enableSorting: false,
            cell: ({ row }: { row: any }) => {
              const attributesArray = [
                // "Processor-socket-type",
                "Processor Socket Type",
                // "Motherboard form factor",
                "Motherboard Form Factor",
                "RAM Type", 
                // "motherboard socket",
                "Motherboard Socket",
                // "Motherboard support",
                // "Motherboard support",
              ];
              const is_mandatory = attributesArray.includes(
                row.original.attribute_name
              ); //*// These mandatory Attributes can not be changed
              return (
                <ul className="d-flex gap-2 list-unstyled mb-0 align-items-center">
                  <li className="d-flex align-items-center">
                    <Link
                      to={
                        is_mandatory
                          ? "#"
                          : `/attribute-form-edit/${row.original.attribute_id}`
                      }
                      state={{ brand: row.original }}
                      className="btn btn-subtle-secondary btn-icon btn-sm edit-item-btn d-flex align-items-center justify-content-center"
                      onClick={(e) => {
                        if (is_mandatory) e.preventDefault();
                        else handleEdit(row.original);
                      }}
                      style={{
                        pointerEvents: is_mandatory ? "none" : "auto",
                        cursor: is_mandatory ? "not-allowed" : "pointer",
                        padding: "0.25rem", // optional: adjust button padding
                      }}
                    >
                      <i
                        className={is_mandatory ? "ph-x-circle" : "ph-pencil"}
                        style={{
                          color: is_mandatory ? "red" : "inherit",
                          fontSize: is_mandatory ? "1.5rem" : "0.75rem",
                          lineHeight: 1,
                        }}
                      ></i>
                    </Link>
                  </li>

                  <li className="d-flex align-items-center">
                    <Link
                      to={`/attribute-config-form/${row?.original?.attribute_id}`}
                      state={{ name: row.original.attribute_name, attribute_id: row.original.attribute_id }}
                      className="btn btn-sm text-black bg-transparent border-0 d-flex align-items-center justify-content-center"
                      title="Configurations"
                      style={{ padding: "0.25rem" }}
                    >
                      <i
                        className="ph-gear-six"
                        style={{ fontSize: "1.5rem", lineHeight: 1 }}
                      ></i>
                    </Link>
                  </li>
                </ul>
              );
            },
          },
        ]
      : [];

  const columns =
    // useMemo(
    //   () =>
    [
      // {
      //   header: "Sr. No.",
      //   id: "sr_no", // important: since there's no accessorKey
      //   enableSorting: false,
      //   enableColumnFilter: false,
      //   cell: ({ row }: { row: any }) => {
      //     return row.index + 1 + pageIndex * pageSize;
      //   },
      // },
      {
        header: "Attributes Name",
        accessorKey: "attribute_name",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl:
          "api/attributes-master/column-filter-dropdown/?column_filter=attribute_name",
      },
      {
        header: "Status",
        accessorKey: "status",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          const pkg = row.original;
          return (
            <div
              style={{ fontSize: "1.5rem", cursor: "pointer" }}
              onClick={() => handleStatusToggle(pkg)}
            >
              <i
                className={`bi ${
                  pkg.status
                    ? "bi-toggle-on text-primary"
                    : "bi-toggle-off text-danger"
                }`}
              />
            </div>
          );
        },
      },
      {
        header: "Created By/Date",
        accessorKey: "created_at",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => {
          const name = row.original.created_by_name ?? "N/A";
          const date = formatDate(row.original.created_at);
          return `  ${name}  -  ${date}  `; // extra spaces for visual padding
        },
      },
      {
        header: "Updated By/Date",
        accessorKey: "updated_at",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => {
          const name = row.original.updated_by_name ?? "N/A";
          const date = formatDate(row.original.updated_at);
          return `  ${name}  -  ${date}  `; // spaces before/after the name
        },
      },
      ...DynamicAction,

      // ...(category_permissions?.Update ? [
    ];
  //   [dispatch, categories]
  // );

  return (
    <Fragment>
      <div className="page-content">
        <Container fluid>
          <Row id="invoiceList">
            <Col lg={12}>
              <Card>
                <Card.Header>
                  <Row className="align-items-center g-2">
                    <Col lg={3} className="me-auto">
                      <h6 className="card-title mb-0">Attributes</h6>
                    </Col>

                    {/* Search Input Box (reusable)*/}
                    <Col xl={2} md={3}>
                      <SearchBox
                        value={searchInput}
                        onChange={setSearchInput}
                      />
                    </Col>

                    <Col className="col-md-auto">
                      <div className="hstack gap-2">
                        <Button
                          className="btn btn-subtle-danger d-none"
                          id="remove-actions"
                        >
                          <i className="ri-delete-bin-2-line"></i>
                        </Button>

                        {attributes_permissions?.permissions?.Create ===
                          true && (
                          <Link
                            to="/attribute-form"
                            state={{ attribute_id: categories[0]?.attribute }}
                            className="btn btn-primary"
                          >
                            <i className="bi bi-plus-circle align-baseline me-1"></i>{" "}
                            Add Attributes
                          </Link>
                        )}
                      </div>
                    </Col>
                  </Row>
                </Card.Header>
                <Card.Body className="mt-3">
                  <LoaderErrorWrapper loading={loading} error={error}>
                    <TableContainer
                      columns={columns}
                      data={data}
                      totalRecords={rowCount}
                      pageIndex={pageIndex}
                      pageSize={pageSize}
                      // onPageChange={setPageIndex}
                      onPageChange={handlePageChange} // for persisted pagination across pages
                      onPageSizeChange={setPageSize}
                      columnFilters={columnFilters}
                      setColumnFilters={setColumnFilters}
                      sorting={sorting}
                      onSortingChange={setSorting}
                      isBordered={false}
                      isPagination={true}
                      isGlobalFilter={false}
                      SearchPlaceholder="Search categories..."
                      manualSorting={true}
                      colValue={setFilterValue}
                    />
                  </LoaderErrorWrapper>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </Fragment>
  );
};

export default AttributesLList;
